"use client";

import { Images } from "lucide-react";

import { useMediaGallery } from "@/components/experience-media-gallery";
import { useTranslator } from "@/lib/translation-context";

import { RailDots, useRailIndex } from "../_rail-dots.client";

import styles from "./gallery-carousel-overlay.module.css";

interface HeroCarouselOverlayProps {
  /** The scroller this overlay reports on. Looked up by id rather than by ref
   *  because the track is server-rendered — a ref cannot cross that boundary,
   *  and lifting the whole gallery to the client to share one would put the LCP
   *  image behind hydration. */
  trackId: string;
  total: number;
  /** Already carries the tour name; the caller owns the translator.
   *
   *  The counter's own "{n} / {total}" is translated HERE instead, because a
   *  function cannot cross the server/client boundary — passing one down
   *  throws "Functions cannot be passed directly to Client Components" at
   *  render, which on this page means the error boundary replaces the hero. */
  openGalleryLabel: string;
}

/**
 * The guide's `.hero-dots` + `.hero-count`, for the mobile hero carousel.
 *
 * Mobile only (hidden from `md`): above that breakpoint the hero is the bento grid,
 * which does not scroll and has the "View all media" chip instead.
 */
export function HeroCarouselOverlay({
  trackId,
  total,
  openGalleryLabel,
}: HeroCarouselOverlayProps) {
  // The SAME index the dots draw, read once and used twice — the counter and
  // the dot row must never disagree about which photo is on screen.
  const index = useRailIndex(trackId, total);
  const { openByIndex, prewarm } = useMediaGallery();
  const t = useTranslator();

  if (total <= 1) return null;

  return (
    <div className={styles.overlay}>
      {/* ALWAYS, at any photo count. This used to be suppressed past twelve
          photos, on the reasoning that twenty dots read as a texture rather
          than a position — which is true of twenty dots, and is why `RailDots`
          windows instead. The cost of suppressing them was that the galleries
          with the MOST to scroll were the ones that lost the only cue saying
          so, and a tour with twenty photos is not unusual. */}
      <RailDots
        trackId={trackId}
        total={total}
        tone="light"
        className={styles.dots}
      />

      {/* NO `aria-label` here. It used to carry `openGalleryLabel` alone, which
          fails WCAG 2.5.3 Label in Name: the visible text is "1 / 20" and the
          accessible name did not contain it, so anyone driving the page by
          voice ("click 1 slash 20") could not reach the control, and Lighthouse
          flagged it as `label-content-name-mismatch`.
          The visible count is the name now, with the purpose appended out of
          sight — so the name READS "1 / 20 Open photo gallery for …", which
          contains the visible string and still says what the button does. */}
      <button
        type="button"
        onPointerEnter={prewarm}
        onFocus={prewarm}
        onClick={() => openByIndex(index)}
        className={styles.counter}
      >
        <Images aria-hidden strokeWidth={1.75} />
        {t("{n} / {total}", { n: index + 1, total })}
        <span className="tui-sr-only">{openGalleryLabel}</span>
      </button>
    </div>
  );
}
